// Stale-chunk / version-skew recovery, shared by the global error handlers
// (src/index.js) and the React error boundary (src/components/ErrorBoundary.js).
//
// After a deploy, a still-open tab holds one bundle version. When it lazy-loads a
// code-split chunk it can get a mismatched version (or a pruned chunk URL that the
// SPA rewrite serves index.html for). Two symptoms result:
//   1. the chunk fails to load -> "Loading chunk N failed" / ChunkLoadError
//   2. the chunk loads but an inter-module reference is undefined and gets
//      called/read -> a minified TypeError ("v is not a function",
//      "Cannot read properties of undefined") thrown from inside a *.chunk.js
// Both are fixed by reloading to a consistent bundle set. (RW-859)
//
// RW-243 Phase 3a gave us counter-based escalation + bail-out. RW-859 generalizes
// it: case (1) was already caught by message; case (2) was NOT (the dominant prod
// error was "v is not a function" on /admin) and slipped through as a real crash.

const CHUNK_RELOAD_KEY = 'chunk-reload-attempts';
const CHUNK_RELOAD_WINDOW_MS = 60 * 1000;
const SHELL_CACHE_NAME = 'shell-reve-v1';

// Generic minified TypeErrors that version-skew produces. We only treat these as
// recoverable when the stack points inside a hashed code-split chunk (*.chunk.js)
// — never on a plain app-logic TypeError in the main bundle, and never on
// unrelated errors like permission-denied. The attemptChunkRecovery counter bails
// out after repeated reloads, so even a genuine bug that happens to live in a
// chunk still surfaces (gets logged) instead of looping.
const VERSION_SKEW_PATTERNS = [
  'is not a function',
  'Cannot read properties of undefined',
  'Cannot read property', // older Chrome/Firefox phrasing
  'undefined is not an object', // Safari phrasing
];

export function isChunkError(message, stack) {
  if (!message) return false;
  if (
    message.includes('ChunkLoadError') ||
    message.includes("Unexpected token '<'") ||
    message.includes('Loading chunk') ||
    message.includes('dynamically imported module')
  ) {
    return true;
  }
  const inHashedChunk = typeof stack === 'string' && stack.includes('.chunk.js');
  if (inHashedChunk && VERSION_SKEW_PATTERNS.some((p) => message.includes(p))) {
    return true;
  }
  return false;
}

async function clearCachesAndReload(target) {
  // Best-effort cleanup. Wrap each step so a single failure (e.g., an older
  // browser without the caches API) doesn't block the reload itself.
  try {
    if (typeof caches !== 'undefined') {
      if (target === 'all') {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } else {
        await caches.delete(target);
      }
    }
  } catch (e) { /* ignore */ }
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) { /* ignore */ }
  window.location.reload();
}

// Counter-based escalation. Reload once clearing the shell cache, again clearing
// all caches + SWs, then bail out: the reload didn't fix it, so the user is
// genuinely stuck. Log THAT case (not every transient hit) so errorLogs->Linear
// only files a ticket for real stuck users, not for the post-deploy stale-chunk
// noise that the reload silently recovers (Rule 17 — recovered != outage).
// `log` is injected (logErrorToFirestore) to keep this module dependency-free.
export function attemptChunkRecovery({ message, stack, log } = {}) {
  let attempts = [];
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (raw) attempts = JSON.parse(raw);
    if (!Array.isArray(attempts)) attempts = [];
  } catch { attempts = []; }
  const now = Date.now();
  attempts = attempts.filter((t) => now - t < CHUNK_RELOAD_WINDOW_MS);
  attempts.push(now);
  try { sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(attempts)); } catch { /* ignore */ }

  if (attempts.length === 1) {
    clearCachesAndReload(SHELL_CACHE_NAME);
  } else if (attempts.length === 2) {
    clearCachesAndReload('all');
  } else {
    if (typeof log === 'function') {
      log({
        type: 'chunk',
        message: `chunk auto-recovery failed after ${attempts.length - 1} reload(s): ${message || 'unknown'}`,
        stack: stack || '',
        userId: 'anonymous',
        route: (typeof window !== 'undefined' && window.location ? window.location.pathname : ''),
      });
    }
    try {
      window.alert(
        'This page failed to load after multiple attempts. Please hard-refresh (Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows) to load the latest version.'
      );
    } catch { /* ignore */ }
  }
}
